import type { ReactNode } from 'react';
import {
  isFilled,
  type ImageField,
  type LinkField,
  type RichTextField,
} from '@prismicio/client';
import { PrismicNextImage } from '@prismicio/next';
import { PrismicRichText } from '@prismicio/react';
import { OutlineCtaLink } from '@/components/common/OutlineCtaLink';
import { cn } from '@/lib/utils';
import { CarouselNavButtons } from './CarouselNavButtons';

export { CarouselNavButtons } from './CarouselNavButtons';

type Props = {
  icon?: ImageField | null;
  /**
   * Card title — accepts either a plain string (Text field) or a Prismic
   * `RichTextField`. Rendered with the same visual treatment either way so
   * variants can pick whichever field type fits their model.
   */
  title?: string | RichTextField | null;
  description?: RichTextField | null;
  /** Outlined CTA link (optional). */
  cta?: LinkField | null;
  atStart: boolean;
  atEnd: boolean;
  onPrev: () => void;
  onNext: () => void;
  /**
   * Extra classes on the content block itself. The default fixes the block at
   * `md:w-[496px]` — variants that host a side-by-side media panel keep that
   * width, while variants that stand alone can widen it (e.g. `md:w-full`).
   */
  className?: string;
  /**
   * Optional slot rendered *after* the content block, inside the same
   * card outer wrapper. Used by the `ProcessOverviewThreeStep` redesign to
   * place a video / image beside the copy.
   */
  mediaSlot?: ReactNode;
};

const TITLE_CLASSNAME =
  'text-[22px] font-medium leading-[1.5] text-[#041118] md:text-[24px]';

/**
 * Shared content block for phase-style carousel cards: prev/next nav row on
 * top, a green icon tile, title, rich-text description, and an optional
 * outlined CTA pinned below.
 *
 * Callers own the outer card wrapper (widths, `data-phase-card`, spacing
 * between cards) so each carousel can size its cards for its own container.
 */
export function PhaseCarouselCard({
  icon,
  title,
  description,
  cta,
  atStart,
  atEnd,
  onPrev,
  onNext,
  className,
  mediaSlot,
}: Props) {
  return (
    <>
      <div
        className={cn(
          'relative flex shrink-0 flex-col gap-7 overflow-hidden bg-[#f3f3f3] p-6 md:w-[496px]',
          className,
        )}
      >
        <CarouselNavButtons
          atStart={atStart}
          atEnd={atEnd}
          onPrev={onPrev}
          onNext={onNext}
        />

        <div className="flex w-full flex-col gap-2">
          {isFilled.image(icon) && (
            <div className="relative flex size-[66px] items-center justify-center rounded-[5px] bg-[#05f273]">
              <PrismicNextImage
                field={icon}
                className="size-10 object-contain"
                fallbackAlt=""
              />
            </div>
          )}
          {typeof title === 'string'
            ? title && <p className={TITLE_CLASSNAME}>{title}</p>
            : isFilled.richText(title) && (
                <PrismicRichText
                  field={title}
                  components={{
                    heading1: ({ children }) => (
                      <p className={TITLE_CLASSNAME}>{children}</p>
                    ),
                    heading2: ({ children }) => (
                      <p className={TITLE_CLASSNAME}>{children}</p>
                    ),
                    heading3: ({ children }) => (
                      <p className={TITLE_CLASSNAME}>{children}</p>
                    ),
                    heading4: ({ children }) => (
                      <p className={TITLE_CLASSNAME}>{children}</p>
                    ),
                    paragraph: ({ children }) => (
                      <p className={TITLE_CLASSNAME}>{children}</p>
                    ),
                  }}
                />
              )}
          {isFilled.richText(description) && (
            <PrismicRichText
              field={description}
              components={{
                paragraph: ({ children }) => (
                  <p className="w-full text-[16px] font-light leading-[1.5] text-[#041118] md:text-[18px]">
                    {children}
                  </p>
                ),
              }}
            />
          )}
          {isFilled.link(cta) && (
            <div className="mt-5">
              <OutlineCtaLink field={cta} tone="light" />
            </div>
          )}
        </div>
      </div>
      {mediaSlot}
    </>
  );
}
